<script setup lang="ts">
import dayjs from 'dayjs'

import { parseDate, type DateValue } from '@internationalized/date'
import { Calendar as CalendarIcon, Copy, Plus, Trash2, X } from 'lucide-vue-next'

import { ACTIVE_DAYS } from '@/types/offer'
import type { TimeSlot } from '@/types/offer'
import { getSampleDateForDay } from '@/utils/offerHelpers'
import { useScheduleSlots } from '@/composables/useScheduleSlots'
import OfferTimeSelect from './OfferTimeSelect.vue'

const props = withDefaults(
  defineProps<{
    isEditMode?: boolean
  }>(),
  { isEditMode: false }
)

const { t, locale } = useI18n()

const scheduleStartDate = defineModel<string | null>('scheduleStartDate', { required: true })
const scheduleEndDate = defineModel<string | null>('scheduleEndDate', { required: true })
const timeSlots = defineModel<TimeSlot[]>('timeSlots', { required: true })

const { hasTimeSlotsInteracted, hasTimeSlotsError, toggleDayForSlot, addTimeSlot, removeTimeSlot, duplicateTimeSlot } =
  useScheduleSlots(timeSlots)

const hasDateInteracted = ref(false)

const emit = defineEmits<{
  (e: 'hasError', value: boolean): void
}>()

const startMinDate = computed(() => (props.isEditMode ? undefined : parseDate(dayjs().format('YYYY-MM-DD'))))

const toCalendarDate = (value: string | null) => (value ? parseDate(value) : undefined)
const fromCalendarDate = (value: DateValue | undefined) => (value ? value.toString() : null)
const formatDisplayDate = (value: string | null) => (value ? dayjs(value).format('DD/MM/YYYY') : null)

const startCalendarDate = computed({
  get: () => toCalendarDate(scheduleStartDate.value),
  set: (value) => {
    scheduleStartDate.value = fromCalendarDate(value)
  }
})

const endCalendarDate = computed({
  get: () => toCalendarDate(scheduleEndDate.value),
  set: (value) => {
    scheduleEndDate.value = fromCalendarDate(value)
  }
})

const updateSlotTime = (slot: TimeSlot, key: 'startTime' | 'endTime', value: string) => {
  slot[key] = value
  hasTimeSlotsInteracted.value = true
}

watch([scheduleStartDate, scheduleEndDate], () => {
  hasDateInteracted.value = true
})

const weekdayFormatterNarrow = computed(() => new Intl.DateTimeFormat(locale.value, { weekday: 'narrow' }))

const dayOptions = computed(() =>
  ACTIVE_DAYS.map((day) => ({
    value: day,
    label: weekdayFormatterNarrow.value.format(getSampleDateForDay(day))
  }))
)

const hasDatesError = computed(() => {
  if (!scheduleStartDate.value) return true
  if (scheduleEndDate.value && scheduleEndDate.value < scheduleStartDate.value) return true
  return false
})

const hasError = computed(() => hasDatesError.value || hasTimeSlotsError.value)

watch(hasError, (val) => emit('hasError', val), { immediate: true })
</script>

<template>
  <Card>
    <CardHeader>
      <CardTitle>{{ t('pages.offer.reward.datesTitle') }}</CardTitle>
    </CardHeader>
    <CardContent class="space-y-6">
      <div>
        <div class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {{ t('pages.offer.reward.validityDates') }}
        </div>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label for="offer-validity-from" class="mb-1 block text-sm font-medium text-foreground">
              {{ t('pages.offer.reward.validityFromLabel') }}
              <span class="text-red-400">*</span>
            </label>
            <Popover>
              <PopoverTrigger as-child>
                <Button
                  id="offer-validity-from"
                  type="button"
                  variant="outline"
                  class="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon />
                  <span :class="{ 'text-muted-foreground': !scheduleStartDate }">
                    {{ formatDisplayDate(scheduleStartDate) ?? t('pages.offer.reward.validityFrom') }}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent class="w-auto p-0" align="start">
                <Calendar v-model="startCalendarDate" :min-value="startMinDate" :locale="locale" />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <label for="offer-validity-until" class="mb-1 block text-sm font-medium text-foreground">
              {{ t('pages.offer.reward.validityUntilLabel') }}
            </label>
            <div class="flex gap-2">
              <Popover>
                <PopoverTrigger as-child>
                  <Button
                    id="offer-validity-until"
                    type="button"
                    variant="outline"
                    class="flex-1 justify-start text-left font-normal"
                  >
                    <CalendarIcon />
                    <span :class="{ 'text-muted-foreground': !scheduleEndDate }">
                      {{ formatDisplayDate(scheduleEndDate) ?? t('pages.offer.reward.validityUntil') }}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent class="w-auto p-0" align="start">
                  <Calendar v-model="endCalendarDate" :min-value="startCalendarDate" :locale="locale" />
                </PopoverContent>
              </Popover>
              <AppTooltip v-if="scheduleEndDate" :text="t('global.words.clear')">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  :aria-label="t('global.words.clear')"
                  @click="scheduleEndDate = null"
                >
                  <X />
                </Button>
              </AppTooltip>
            </div>
            <p class="mt-1 text-xs text-muted-foreground">
              {{ t('pages.offer.reward.endDateEmptyHint') }}
            </p>
          </div>
        </div>
        <p v-if="hasDatesError && hasDateInteracted" class="mt-1 text-xs text-red-500">
          {{ t('pages.offer.reward.datesError') }}
        </p>
      </div>

      <div class="mt-4 self-stretch inline-flex flex-col items-start gap-3 w-full">
        <div class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {{ t('pages.offer.reward.timeSlots') }}
        </div>

        <div class="w-full space-y-4">
          <div
            v-for="slot in timeSlots"
            :key="slot.id"
            class="flex w-full flex-col items-end rounded-xl border border-border bg-white"
          >
            <div
              class="inline-flex h-12 w-full items-center justify-between rounded-tl-xl rounded-tr-xl border-b border-border bg-muted px-4"
            >
              <div class="text-xs font-semibold uppercase leading-4 tracking-wide text-muted-foreground">
                {{ t('pages.offer.reward.timeSlot') }} {{ slot.id }}
              </div>
              <div class="flex items-center gap-1">
                <Button
                  variant="ghost"
                  class="group h-6 w-6 min-w-6 p-0 hover:bg-accent"
                  @click="duplicateTimeSlot(slot.id)"
                >
                  <Copy class="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
                </Button>
                <Button
                  v-if="timeSlots.length > 1"
                  variant="ghost"
                  class="group h-6 w-6 min-w-6 p-0 hover:bg-accent"
                  @click="removeTimeSlot(slot.id)"
                >
                  <Trash2 class="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
                </Button>
              </div>
            </div>

            <div class="flex w-full flex-col items-start gap-6 rounded-bl-xl rounded-br-xl bg-white p-3">
              <div class="flex w-full flex-col items-start gap-1.5">
                <div class="w-full text-sm font-normal leading-6 text-foreground">
                  {{ t('pages.offer.reward.activeDays') }}
                </div>
                <div class="inline-flex w-full flex-wrap items-center gap-3 py-1">
                  <Button
                    v-for="day in dayOptions"
                    :key="day.value"
                    :variant="slot.days.includes(day.value) ? 'default-primary' : 'outline'"
                    size="icon"
                    @click="(e) => toggleDayForSlot(slot.id, day.value, e)"
                  >
                    {{ day.label }}
                  </Button>
                </div>
              </div>

              <div class="flex w-full flex-col justify-start gap-5 md:flex-row md:items-start">
                <div class="flex-1 inline-flex flex-col items-start justify-end gap-1.5">
                  <div class="w-full text-sm font-normal leading-6 text-foreground">
                    {{ t('pages.offer.reward.startTime') }}
                  </div>
                  <OfferTimeSelect
                    :model-value="slot.startTime"
                    @update:model-value="updateSlotTime(slot, 'startTime', $event)"
                  />
                </div>
                <div class="flex-1 inline-flex flex-col items-start justify-end gap-1.5">
                  <div class="w-full text-sm font-normal leading-6 text-foreground">
                    {{ t('pages.offer.reward.endTime') }}
                  </div>
                  <OfferTimeSelect
                    :model-value="slot.endTime"
                    @update:model-value="updateSlotTime(slot, 'endTime', $event)"
                  />
                </div>
              </div>
            </div>
          </div>

          <p v-if="hasTimeSlotsError && hasTimeSlotsInteracted" class="mt-2 text-xs text-red-500">
            {{ t('pages.offer.reward.timeSlotsError') }}
          </p>
        </div>

        <Button variant="outline" class="mt-4 w-full" @click="addTimeSlot">
          <Plus />
          {{ t('pages.offer.reward.addTimeSlot') }}
        </Button>
      </div>
    </CardContent>
  </Card>
</template>
